import { FC, useState, useCallback, useRef, useEffect } from 'react';

import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import InputBase from '@mui/material/InputBase';

import type { ConversationRecord } from '../../types';
import { useConversationItemStyles } from './styles/conversation-item.styles';
import { ConversationMenuCard } from './ConversationMenuCard';
import { DeleteConversationDialog } from './DeleteConversationDialog';
import { mcpChatTranslationRef } from '../../translation';
import { SlidingTitle } from './SlidingTitle';

interface ConversationItemProps {
  /** The conversation to display */
  conversation: ConversationRecord;
  /** Whether this conversation is currently selected */
  isSelected: boolean;
  /** Called when the conversation is clicked */
  onSelect: () => void;
  /** Called when the star button is clicked */
  onToggleStar: () => void;
  /** Called when delete is confirmed */
  onDelete: () => void;
  /** Called when rename is confirmed */
  onRename: (title: string) => void;
}

export const ConversationItem: FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  onSelect,
  onToggleStar,
  onDelete,
  onRename,
}) => {
  const classes = useConversationItemStyles();
  const { t } = useTranslationRef(mcpChatTranslationRef);
  const [deleteAnchor, setDeleteAnchor] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Get display title - use title if available, fallback to first user message
  const getDisplayTitle = useCallback((): string => {
    if (conversation.title) {
      return conversation.title;
    }
    const firstUserMessage = conversation.messages.find(m => m.role === 'user');
    const content =
      firstUserMessage?.content || t('conversationItem.emptyConversation');
    return content.length > 40 ? `${content.substring(0, 40)}...` : content;
  }, [conversation.title, conversation.messages, t]);

  const handleRenameStart = useCallback(() => {
    setEditValue(getDisplayTitle());
    setIsEditing(true);
  }, [getDisplayTitle]);

  const handleRenameConfirm = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== getDisplayTitle()) {
      onRename(trimmed);
    }
    setIsEditing(false);
    (document.activeElement as HTMLElement)?.blur();
  }, [editValue, onRename, getDisplayTitle]);

  const handleRenameCancel = useCallback(() => {
    setIsEditing(false);
    (document.activeElement as HTMLElement)?.blur();
  }, []);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRenameConfirm();
      } else if (e.key === 'Escape') {
        handleRenameCancel();
      }
    },
    [handleRenameConfirm, handleRenameCancel],
  );

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setDeleteAnchor(event.currentTarget);
    },
    [],
  );

  const handleDeleteClose = useCallback(() => {
    if (deleteAnchor && deleteAnchor.parentNode) {
      deleteAnchor.parentNode.removeChild(deleteAnchor);
    }
    setDeleteAnchor(null);
  }, [deleteAnchor]);

  const handleDeleteConfirm = useCallback(() => {
    handleDeleteClose();
    onDelete();
  }, [handleDeleteClose, onDelete]);

  const getFullTitle = useCallback((): string => {
    if (conversation.title) return conversation.title;
    const firstUserMessage = conversation.messages.find(m => m.role === 'user');
    return firstUserMessage?.content || t('conversationItem.emptyConversation');
  }, [conversation.title, conversation.messages, t]);

  const showActions = isHovered || Boolean(deleteAnchor);

  return (
    <ListItem
      disablePadding
      className={classes.listItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ListItemButton
        selected={isSelected}
        onClick={isEditing ? undefined : onSelect}
        className={classes.listItemButton}
      >
        {/* Content */}
        <Box
          className={`${classes.content} ${showActions ? classes.contentWithActions : ''}`}
        >
          {isEditing ? (
            <InputBase
              inputRef={inputRef}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={handleRenameConfirm}
              onClick={e => e.stopPropagation()}
              fullWidth
              className={classes.renameInput}
              inputProps={{ maxLength: 255 }}
            />
          ) : (
            <SlidingTitle
              text={getFullTitle()}
              active={isHovered}
              className={`${classes.title} ${isHovered ? classes.titleHovered : ''}`}
              innerClassName={classes.titleInner}
            />
          )}
        </Box>

        {/* More actions menu - shown on hover or selection */}
        {showActions && !isEditing && (
          <Box
            className={classes.actionsContainer}
            onClick={e => e.stopPropagation()}
          >
            <ConversationMenuCard
              isStarred={conversation.isStarred}
              onToggleStar={onToggleStar}
              onClick={handleDeleteClick}
              onRename={handleRenameStart}
            />
          </Box>
        )}
      </ListItemButton>

      {/* Delete confirmation dialog */}
      <DeleteConversationDialog
        open={Boolean(deleteAnchor)}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </ListItem>
  );
};
